import { Children, cloneElement, Fragment, isValidElement, type ReactNode } from "react";
import { DisplayHeading, SectionHeader } from "./primitives";
import { ProcessSteps } from "./ProcessSteps";
import { IndustryList } from "./IndustryList";
import { StatRow } from "./StatRow";
import { QuestionBody } from "./QuestionBody";

type NodeProps = { children?: ReactNode; className?: string; style?: { gridTemplateColumns?: string }; as?: string };
function props(node: ReactNode): NodeProps {
  return isValidElement<NodeProps>(node) ? node.props : {};
}
function name(node: ReactNode) {
  if (!isValidElement(node)) return "";
  if (node.type === SectionHeader) return "SectionHeader";
  if (node.type === DisplayHeading) return "DisplayHeading";
  if (node.type === ProcessSteps) return "ProcessSteps";
  if (node.type === IndustryList) return "IndustryList";
  if (node.type === StatRow) return "StatRow";
  return typeof node.type === "string" ? node.type : "";
}
function heading(node: ReactNode): boolean {
  return name(node) === "h2" || name(node) === "SectionHeader" || (name(node) === "DisplayHeading" && props(node).as === "h2");
}
function wide(node: ReactNode): boolean {
  const p = props(node);
  return /(?:sm|md|lg):grid-cols-|(?:^|\s)grid-cols-[2-9]/.test(p.className ?? "") || Boolean(p.style?.gridTemplateColumns) || ["ProcessSteps", "IndustryList", "StatRow"].includes(name(node)) || Children.toArray(p.children).some(wide);
}
function authoredNodes(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((node) => isValidElement<NodeProps>(node) && node.type === Fragment ? authoredNodes(node.props.children) : [node]);
}

/** Classify the section's authored React markup, never move rendered DOM nodes. */
export function SectionLayout({ children, className = "", ...rest }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  const nodes = Children.toArray(children);
  const first = nodes[0];
  if (!first || wide(first)) return <div className={`wrap ${className}`} {...rest}>{children}</div>;
  let head: ReactNode[] = [first];
  let body = nodes.slice(1);
  if (!heading(first)) {
    const inner = Children.toArray(props(first).children);
    const at = inner.findIndex(heading);
    if (at < 0) {
      const direct = nodes.findIndex(heading);
      if (direct < 0) return <div className={`wrap ${className}`} {...rest}>{children}</div>;
      head = nodes.slice(0, direct + 1);
      body = nodes.slice(direct + 1);
    } else {
      head = inner.slice(0, at + 1);
      body = [...inner.slice(at + 1), ...body];
    }
  }
  if (!body.length) return <div className={`wrap ${className}`} {...rest}>{children}</div>;
  if (body.some(wide)) {
    const gridAt = body.findIndex(wide);
    if (gridAt > 0 && name(first) !== "SectionHeader") return <div className={`wrap ${className}`} {...rest}>
      <div className="section-wide-intro"><div>{head}</div><div>{body.slice(0, gridAt)}</div></div>{body.slice(gridAt)}
    </div>;
    return <div className={`wrap ${className}`} {...rest}>{children}</div>;
  }
  if (name(first) === "SectionHeader" && isValidElement<NodeProps>(first) && props(first).children) {
    head = [cloneElement(first, { children: undefined })];
    body = [<div key="intro" className="section-intro mt-6">{props(first).children}</div>, ...body];
  }
  return <div className={`wrap section-split ${className}`} {...rest}>
    <div className="section-heading">{head}</div>
    <div className="section-body"><QuestionBody>{body}</QuestionBody></div>
  </div>;
}

/** Group authored heading/content siblings during render, including research prose. */
export function SectionGroups({ children, className = "", style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  const groups: { title: ReactNode; body: ReactNode[] }[] = [];
  const before: ReactNode[] = [];
  authoredNodes(children).forEach((node) => {
    const inner = authoredNodes(props(node).children);
    if (heading(node)) groups.push({ title: node, body: [] });
    else if (heading(inner[0])) {
      groups.push({ title: inner[0], body: inner.slice(1) });
    }
    else {
      const group = groups.at(-1);
      if (group) group.body.push(node); else before.push(node);
    }
  });
  return <div className={className} style={style}>{before}{groups.map((group, index) =>
    <section key={index} className="section-split grouped-section"><div className="section-heading">{group.title}</div><div className="section-body"><QuestionBody>{group.body}</QuestionBody></div></section>
  )}</div>;
}